import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Modal } from "@/src/components/Modal/Modal";
import AppendInput from "@/src/components/PhoneInput/AppendInput";
import { textVariants } from "@/src/components/Text/Text";
import api, { removeUndefinedAndNull } from "@/src/utils";
import { useMutation } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

interface Props {
  data: PaintProfile | undefined;
  open: boolean;
  setOpen: (val: boolean) => void;
}

const formState = z.object({
  new_code: z.string(),
  new_email: z.string(),
  old_code: z.string(),
});

const ChangeEmail = ({ data, open, setOpen }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) =>
    t(`app.settings.securityCenter.verificationMethods.email.modal.${key}`);
  type formType = z.infer<typeof formState>;
  // const [modal, setModal] = useUrlState("modal");
  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
    watch,
  } = useForm<formType>();

  const change = async () => {
    const res = await api.post(apiRoutes.userEmailChange, {
      email: data?.email,
      ...getValues(),
      type: "change",
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: change,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setOpen(false);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const sendOtp = async (type: string) => {
    const res = await api.post(
      apiRoutes.userEmailChange,
      removeUndefinedAndNull({
        type: type === "old" ? "request_old" : "request_new",
        new_email: type === "old" ? undefined : getValues().new_email,
      })
    );
    return res.data;
  };

  const { mutate: mutateOtp, isLoading: otpLoad } = useMutation({
    mutationFn: sendOtp,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submit: SubmitHandler<formType> = () => {
    mutate();
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={trans("title")}
      setter="state"
      footer={
        <Button loading={isLoading} onClick={handleSubmit(submit)}>
          {trans("form.submitButton")}
        </Button>
      }
    >
      <div className="grid gap-4">
        {/* <InputComponent
          readOnly
          // value={data?.email}
          label={data?.email}
          className="text-base font-medium"
        /> */}
        <AppendInput
          {...register("old_code")}
          // errors={errors.new_code?.message}
          label={data?.email}
          right={
            <div className="py-2 pr-2">
              <button
                onClick={() => mutateOtp("old")}
                className={cn(
                  textVariants({ variant: "link", size: "sm" }),
                  "whitespace-nowrap cursor-pointer text-xs "
                )}
              >
                {otpLoad ? trans("form.sending") : trans("form.getCode")}
              </button>
            </div>
          }
        />
        <InputComponent
          {...register("new_email")}
          label={trans("form.newEmail.label")}
          type="email"
        />
        <AppendInput
          {...register("new_code")}
          errors={errors.new_code?.message}
          label={trans("form.verificationCode.label")}
          right={
            <div className="py-2 pr-2">
              <button
                disabled={isEmpty(watch("new_email"))}
                onClick={() => mutateOtp("new")}
                className={cn(
                  textVariants({ variant: "link", size: "sm" }),
                  "whitespace-nowrap cursor-pointer text-xs "
                )}
              >
                {otpLoad ? trans("form.sending") : trans("form.getCode")}
              </button>
            </div>
          }
        />
      </div>
    </Modal>
  );
};

export default ChangeEmail;
